"use client";

/**
 * The payments-paused pop-up — what a buyer sees the moment their pay click is
 * intercepted, and what happens when they proceed.
 *
 * TWO STATES, and the second is the point. The button says "proceed", so it has
 * to proceed to something: it POSTs the blocked purchase to
 * /api/billing/payment-request, which writes the row support drains. Only once
 * that lands do we show "You're in the queue" and promise an email. If the write
 * fails we show the support address instead — never the queued state — because
 * telling someone they are handled when no record of them exists is the one
 * failure this whole feature exists to prevent.
 *
 * Rendered by <PaymentsPausedHost /> at the app root; every pay handler reaches
 * it through `interceptIfPaymentsPaused(...)`.
 *
 * Built on the base-ui primitives rather than <DialogContent> because of the
 * z-index: nearly every pay button in the app lives INSIDE another modal (the
 * plan picker, the rated flow at z-[100]), so a dialog on the house default
 * (z-70 / md:z-50) mounts BEHIND the very modal that opened it — verified in the
 * real app before this was raised. z-[9999] matches the other root-mounted
 * global gate (auth/email-verification-gate-modal.tsx).
 *
 * Colour follows components/ui/modal-accents.ts: the step-1 badge is AMBER
 * because a service disruption is a disclosure the user must notice; the step-2
 * badge and both CTAs are the house GREEN because proceeding is the forward,
 * repeatable action.
 */
import { useCallback, useEffect, useState } from "react";
import { Check, Clock, Loader2, Mail, X } from "lucide-react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";

import { cn } from "@/lib/utils";
import { MODAL_CTA_SUCCESS, MODAL_CTA_SUCCESS_BUSY } from "@/components/ui/modal-accents";
import {
  MODAL_SCROLL_BODY,
  MODAL_VIEWPORT_FRAME,
} from "@/components/ui/modal-surface";
import { createClient } from "@/lib/supabase/client";
import {
  PAYMENTS_PAUSED_COPY,
  pausedIntentLabel,
  pausedQueuedBody,
  pausedSupportMailto,
} from "@/lib/billing/payments-paused";
import {
  trackPaymentsPausedQueued,
  trackPaymentsPausedQueueFailed,
} from "@/lib/analytics";
import type { PausedPurchaseRequest } from "./payments-paused-store";

type Phase = "notice" | "saving" | "queued" | "failed";

export function PaymentsPausedModal({
  request,
  onDismiss,
}: {
  request: PausedPurchaseRequest;
  onDismiss: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("notice");
  const [accountEmail, setAccountEmail] = useState<string | null>(null);

  // The signed-in address, named back in the queued state so the buyer knows
  // WHERE the promised email is going. `getSession()` reads the local session —
  // no network round trip — and the server stamps the row with its own copy, so
  // a missing value here only softens the copy, never the record.
  useEffect(() => {
    let cancelled = false;
    createClient()
      .auth.getSession()
      .then(({ data }) => {
        if (!cancelled) setAccountEmail(data.session?.user?.email ?? null);
      })
      .catch(() => {
        /* no session — the queued copy drops the address clause */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleProceed = useCallback(async () => {
    setPhase("saving");
    try {
      const res = await fetch("/api/billing/payment-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: request.kind,
          surface: request.surface,
          plan: request.plan ?? null,
          cadence: request.cadence ?? null,
          credits: request.credits ?? null,
          amountUsd: request.amountUsd ?? null,
          label: request.label ?? null,
          priceLabel: request.priceLabel ?? null,
        }),
      });
      const data = (await res.json().catch(() => null)) as
        | { ok?: boolean; email?: string | null; duplicate?: boolean }
        | null;

      if (!res.ok || !data?.ok) {
        trackPaymentsPausedQueueFailed({
          intent: request.kind,
          surface: request.surface,
          status: res.status,
        });
        setPhase("failed");
        return;
      }

      // Prefer the server's address — it read the session server-side and is
      // the one actually stamped on the row we will mail.
      if (data.email) setAccountEmail(data.email);
      trackPaymentsPausedQueued({
        intent: request.kind,
        surface: request.surface,
        plan: request.plan ?? null,
        amountUsd: request.amountUsd ?? null,
        duplicate: Boolean(data.duplicate),
      });
      setPhase("queued");
    } catch {
      trackPaymentsPausedQueueFailed({
        intent: request.kind,
        surface: request.surface,
        status: 0,
      });
      setPhase("failed");
    }
  }, [request]);

  const queued = phase === "queued";
  const hasIntentRow = Boolean(request.label || request.priceLabel);

  return (
    <DialogPrimitive.Root
      open
      // While the request is in flight the dialog must not be dismissable —
      // closing mid-POST would leave the buyer unsure whether it saved.
      onOpenChange={(o) => {
        if (!o && phase !== "saving") onDismiss();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop
          className={cn(
            "fixed inset-0 z-[9999] bg-black/50 supports-backdrop-filter:backdrop-blur-sm",
            "data-open:animate-in data-open:fade-in-0",
            "data-closed:animate-out data-closed:fade-out-0",
          )}
        />
        <DialogPrimitive.Popup
          className={cn(
            "fixed left-1/2 top-1/2 z-[9999] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2",
            "rounded-2xl bg-popover text-popover-foreground ring-1 ring-foreground/10 shadow-2xl outline-none",
            // Containment, per components/ui/modal-surface.ts. Measured at
            // 320x480 before this: the card was 512px tall and spilled past BOTH
            // edges with nothing scrolling, clipping the badge off the top and
            // the fine print off the bottom. The popup is the frame and never
            // scrolls; the body below is the single scroller, which keeps the ✕
            // pinned at every scroll position.
            MODAL_VIEWPORT_FRAME,
            "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
            "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
          )}
        >
          <button
            type="button"
            onClick={onDismiss}
            disabled={phase === "saving"}
            aria-label="Close"
            className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>

          <div
            className={cn(
              "flex flex-col items-center gap-3 px-6 pb-5 pt-8 text-center",
              MODAL_SCROLL_BODY,
            )}
          >
            <div
              className={cn(
                "flex h-11 w-11 items-center justify-center rounded-2xl ring-1",
                queued
                  ? "bg-emerald-50 text-emerald-600 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/30"
                  : "bg-amber-50 text-amber-600 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/30",
              )}
            >
              {queued ? (
                <Check className="h-5 w-5" strokeWidth={2.75} />
              ) : (
                <Clock className="h-5 w-5" />
              )}
            </div>

            <DialogPrimitive.Title className="text-balance text-lg font-semibold tracking-tight">
              {queued
                ? PAYMENTS_PAUSED_COPY.queuedTitle
                : PAYMENTS_PAUSED_COPY.title}
            </DialogPrimitive.Title>

            <DialogPrimitive.Description className="max-w-[42ch] text-[13px] leading-relaxed text-muted-foreground">
              {queued
                ? pausedQueuedBody(accountEmail)
                : PAYMENTS_PAUSED_COPY.body}
            </DialogPrimitive.Description>

            {/* What they were buying. Omitted when the call site could not tell
                us (e.g. a card-setup click, where nothing is being bought) — an
                empty labelled box reads as a bug. */}
            {hasIntentRow && (
              <div className="mt-1 flex w-full items-center justify-between gap-3 rounded-xl border border-border bg-muted/50 px-3 py-2.5 text-left">
                <div className="min-w-0">
                  <div className="text-[11px] text-muted-foreground">
                    {queued
                      ? PAYMENTS_PAUSED_COPY.queuedIntentLabel
                      : pausedIntentLabel(request.kind)}
                  </div>
                  {request.label && (
                    <div className="truncate text-[13px] font-semibold">
                      {request.label}
                    </div>
                  )}
                </div>
                {request.priceLabel && (
                  <div className="shrink-0 text-[15px] font-bold tracking-tight tabular-nums">
                    {request.priceLabel}
                  </div>
                )}
              </div>
            )}

            {/* With delay framing the buyer's first assumption is that money has
                already left their account. Said in both states. */}
            <p className="text-[11.5px] font-semibold text-emerald-700 dark:text-emerald-400">
              {queued
                ? PAYMENTS_PAUSED_COPY.queuedNoCharge
                : PAYMENTS_PAUSED_COPY.noCharge}
            </p>

            {phase === "failed" && (
              <p className="max-w-[40ch] text-[12.5px] leading-relaxed text-amber-700 dark:text-amber-400">
                {PAYMENTS_PAUSED_COPY.saveFailed}
              </p>
            )}

            <div className="mt-1 flex w-full flex-col gap-2">
              {queued ? (
                <button
                  type="button"
                  onClick={onDismiss}
                  className={cn(
                    "flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold",
                    MODAL_CTA_SUCCESS,
                  )}
                >
                  {PAYMENTS_PAUSED_COPY.queuedCta}
                </button>
              ) : phase === "failed" ? (
                <a
                  href={pausedSupportMailto(request, accountEmail)}
                  onClick={onDismiss}
                  className={cn(
                    "flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold",
                    MODAL_CTA_SUCCESS,
                  )}
                >
                  <Mail className="h-4 w-4" />
                  {PAYMENTS_PAUSED_COPY.saveFailedCta}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={handleProceed}
                  disabled={phase === "saving"}
                  aria-busy={phase === "saving"}
                  className={cn(
                    "flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold",
                    MODAL_CTA_SUCCESS,
                    MODAL_CTA_SUCCESS_BUSY,
                  )}
                >
                  {phase === "saving" && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {phase === "saving"
                    ? PAYMENTS_PAUSED_COPY.ctaBusy
                    : PAYMENTS_PAUSED_COPY.cta}
                </button>
              )}

              {phase === "notice" && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className="rounded-full py-1.5 text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
                >
                  {PAYMENTS_PAUSED_COPY.dismiss}
                </button>
              )}
            </div>

            {queued && (
              <p className="text-[11px] text-muted-foreground">
                {PAYMENTS_PAUSED_COPY.queuedFinePrint}
              </p>
            )}
          </div>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
